// ============================================================================
// Cocoon Nest · NotificationEngine.jsx  (Iter-312 · Notification Isolation — IMMUTABLE)
// ----------------------------------------------------------------------------
// Master Rule: All Automated Email + WhatsApp alert plumbing AND the PNG
// dashboard snapshot capture (html2canvas, iframe print-route rasteriser)
// live strictly in this file. Reporting math and notification snapshots
// are IMMUTABLE during UI tasks (see SYSTEM_RULES.md).
//
// Scope — this engine owns:
//   • Client-side PNG snapshot capture:
//        - `useDashboardSnapshot`      · in-place html2canvas of a DOM root
//                                        driven by the "Download Dashboard"
//                                        buttons on every dashboard.
//        - `captureBranchOverviewPng`  · hidden-iframe rasteriser used by
//        - `captureBranchOverviewRanges` the Automated Email pipeline
//                                        (Start-of-Day + Test Email) so the
//                                        outbound PNG is byte-identical to
//                                        the file the operator downloads.
//   • Snapshot filename + banner label helpers
//        - `formatSnapshotDateLabel`
//        - `buildSnapshotFilename`
//   • Dispatch helpers (server-side routing wrappers):
//        - `dispatchStartOfDay`        · POSTs `/branches/{bid}/business-date`
//                                        with the captured PNG payload.
//        - `dispatchTestNotification`  · Captures the rule's branch PNG then
//                                        POSTs `/notification-rules/{rid}/test`.
//        - `dispatchWhatsappViaWati`   · Thin shim over the backend WATI
//                                        webhook proxy. Reserved for future
//                                        rules whose transport is WhatsApp
//                                        rather than email.
//
// Purity Contract:
//   - `formatSnapshotDateLabel` and `buildSnapshotFilename` are pure JS.
//   - The capture helpers own the DOM / iframe / html2canvas machinery.
//   - The dispatch helpers are thin wrappers over `staffApi`; they never
//     mutate persisted state directly — all writes go through the backend.
//
// Applies globally to all 8 branches — the helpers are data-driven and
// don't know or care which rule / dashboard is mounted.
//
// Consumers (Iter-312):
//   • components/StartOfDayGate.jsx
//   • pages/staff/branch/BranchOverview.jsx
//   • pages/staff/brand/BrandDashboard.jsx
//   • pages/staff/brand/BrandNotifications.jsx
// ============================================================================

import { useCallback, useEffect, useRef, useState } from "react";
import html2canvas from "html2canvas";
import { toast } from "sonner";
import { staffApi } from "@/lib/api";

const CAPTURE_ATTR = "data-capturing";
const STYLE_ID = "dashboard-snapshot-capture-styles";
const MONTH_ABBR = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const IFRAME_STYLE = {
  position: "fixed",
  left: "-99999px",
  top: "0",
  width: "820px",
  height: "1400px",
  border: "0",
  opacity: "0",
  pointerEvents: "none",
  zIndex: "-1",
};

const PRINT_READY_TIMEOUT_MS = 20000;
const PRINT_READY_POLL_MS = 120;

// ---------------------------------------------------------------------------
// Snapshot filename + banner label helpers (pure JS)
// ---------------------------------------------------------------------------

function toTitleCase(input) {
  const words = String(input || "Dashboard").split(/[^a-zA-Z0-9]+/).filter(Boolean);
  if (!words.length) return "Dashboard";
  return words.map((w) => w[0].toUpperCase() + w.slice(1).toLowerCase()).join("_");
}

function todayLocalIsoDate() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function filenameDatePart(dateFrom) {
  const iso = String(dateFrom || todayLocalIsoDate());
  return iso.replace(/-/g, "_");
}

/**
 * Format a Bali-ISO date range as a human-readable snapshot label.
 * Example: formatSnapshotDateLabel("2026-09-19", "2026-09-19") →
 *          "Report for 19 Sep 2026".
 */
export function formatSnapshotDateLabel(dateFrom, dateTo) {
  const fmt = (iso) => {
    if (!iso) return "";
    const [y, m, d] = String(iso).split("-").map((v) => Number(v));
    if (!y || !m || !d) return String(iso);
    return `${d} ${MONTH_ABBR[m - 1] || ""} ${y}`.trim();
  };
  const from = fmt(dateFrom);
  const to = fmt(dateTo);
  if (!from && !to) return "";
  if (!to || from === to) return `Report for ${from}`;
  return `Report for ${from} – ${to}`;
}

/**
 * Build the download filename. `_Overview` is appended when the label
 * doesn't already carry it, so callers can pass either "Legian" or
 * "Legian Overview" and get a consistent shape either way.
 */
export function buildSnapshotFilename(label, dateFrom) {
  const slug = toTitleCase(label);
  const withSuffix = /overview/i.test(slug) ? slug : `${slug}_Overview`;
  return `${withSuffix}_${filenameDatePart(dateFrom)}.png`;
}

/**
 * Dashboard snapshots that span two periods name the file after the
 * dashboard plus every date in play, e.g.
 * `Cancellation_Canggu_A_2026_10_01_to_2026_10_31_B_...png`.
 */
export function buildRangeSnapshotFilename(label, aFrom, aTo, bFrom, bTo) {
  const parts = [toTitleCase(label), "A", filenameDatePart(aFrom), "to", filenameDatePart(aTo)];
  if (bFrom && bTo) {
    parts.push("B", filenameDatePart(bFrom), "to", filenameDatePart(bTo));
  }
  return `${parts.join("_")}.png`;
}

// ---------------------------------------------------------------------------
// In-place html2canvas capture (button-driven)
// ---------------------------------------------------------------------------

function ensureCaptureStyles() {
  if (typeof document === "undefined") return;
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = `
    [${CAPTURE_ATTR}="true"] .truncate,
    [${CAPTURE_ATTR}="true"] [class*="truncate"] {
      overflow: visible !important;
      text-overflow: clip !important;
      white-space: normal !important;
    }
    [${CAPTURE_ATTR}="true"] .whitespace-nowrap {
      white-space: normal !important;
    }
    [${CAPTURE_ATTR}="true"] [class*="max-w-"] {
      max-width: none !important;
    }
    [${CAPTURE_ATTR}="true"] .overflow-hidden {
      overflow: visible !important;
    }
    [${CAPTURE_ATTR}="true"] .flex.items-center {
      align-items: flex-start !important;
    }
    .snapshot-only { display: none; }
    [${CAPTURE_ATTR}="true"] .snapshot-only { display: block; }
  `;
  document.head.appendChild(style);
}

/**
 * Hook: expose a ref + `downloadPng()` that html2canvases the ref'd
 * node into a PNG the browser downloads instantly. No server round-trip,
 * no persisted-state mutation.
 */
export function useDashboardSnapshot(optionsOrLabel = "Cocoon Dashboard") {
  const opts = typeof optionsOrLabel === "string" ? { label: optionsOrLabel } : (optionsOrLabel || {});
  const label = opts.label || "Cocoon Dashboard";
  const dateFrom = opts.dateFrom || null;
  const dateTo = opts.dateTo || null;
  const filename = opts.filename || null;

  const downloadRef = useRef(null);
  const [capturing, setCapturing] = useState(false);

  useEffect(() => { ensureCaptureStyles(); }, []);

  const downloadPng = useCallback(async () => {
    const node = downloadRef.current;
    if (!node) {
      toast.error("Dashboard is still loading — try again in a moment.");
      return;
    }
    ensureCaptureStyles();
    setCapturing(true);
    node.setAttribute(CAPTURE_ATTR, "true");
    try {
      await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      const scale = Math.max(2, Math.min(4, (typeof window !== "undefined" && window.devicePixelRatio) || 2));
      const canvas = await html2canvas(node, {
        backgroundColor: "#ffffff",
        scale,
        useCORS: true,
        logging: false,
        scrollX: 0,
        scrollY: -window.scrollY,
        width: node.scrollWidth,
        height: node.scrollHeight,
        windowWidth: document.documentElement.scrollWidth,
        windowHeight: Math.max(document.documentElement.scrollHeight, node.scrollHeight),
      });
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
      if (!blob) {
        throw new Error("Snapshot returned an empty image");
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || buildSnapshotFilename(label, dateFrom);
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("Dashboard PNG downloaded");
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Dashboard snapshot failed", err);
      toast.error("Could not generate the dashboard PNG. Please try again.");
    } finally {
      node.removeAttribute(CAPTURE_ATTR);
      setCapturing(false);
    }
  }, [label, dateFrom, dateTo, filename]);

  return { downloadRef, downloadPng, capturing };
}

// ---------------------------------------------------------------------------
// Iframe capture (server-driven, print-route rasteriser)
// ---------------------------------------------------------------------------

async function waitForPrintReady(iframe) {
  const start = Date.now();
  while (Date.now() - start < PRINT_READY_TIMEOUT_MS) {
    const doc = iframe.contentDocument;
    if (doc && doc.documentElement && doc.documentElement.getAttribute("data-print-ready") === "1") {
      const a4 = doc.querySelector(".a4");
      if (a4) return a4;
    }
    await new Promise((r) => setTimeout(r, PRINT_READY_POLL_MS));
  }
  throw new Error("Print snapshot timed out (data-print-ready not set)");
}

/**
 * Capture a Branch Overview dashboard PNG using the print route in a
 * hidden iframe. Returns the base64 payload (no `data:image/png…`
 * prefix) so the caller can attach it directly to the backend POST.
 */
export async function captureBranchOverviewPng(bid, rangeKey = "today") {
  const tokenRes = await staffApi.post("/reports/snapshot-token", {
    kind: "branch_overview",
    bid,
    range_key: rangeKey,
  });
  const { token, date_from: dateFrom, date_to: dateTo } = tokenRes.data || {};
  if (!token) throw new Error("Could not mint snapshot token");
  const url =
    `/staff/branch/${bid}/print/overview` +
    `?token=${encodeURIComponent(token)}` +
    `&range=${encodeURIComponent(rangeKey)}` +
    `&date_from=${encodeURIComponent(dateFrom || "")}` +
    `&date_to=${encodeURIComponent(dateTo || "")}`;

  const iframe = document.createElement("iframe");
  Object.assign(iframe.style, IFRAME_STYLE);
  iframe.setAttribute("aria-hidden", "true");
  iframe.setAttribute("tabindex", "-1");
  iframe.setAttribute("data-testid", `snapshot-iframe-${bid}-${rangeKey}`);
  iframe.src = url;
  document.body.appendChild(iframe);

  try {
    await new Promise((resolve, reject) => {
      let done = false;
      const onLoad = () => { done = true; resolve(); };
      const onError = () => { done = true; reject(new Error("Snapshot iframe failed to load")); };
      iframe.addEventListener("load", onLoad, { once: true });
      iframe.addEventListener("error", onError, { once: true });
      setTimeout(() => {
        if (!done) reject(new Error("Snapshot iframe load timed out"));
      }, PRINT_READY_TIMEOUT_MS);
    });
    const node = await waitForPrintReady(iframe);
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

    const scale = Math.max(2, Math.min(4, (window && window.devicePixelRatio) || 2));
    const canvas = await html2canvas(node, {
      backgroundColor: "#ffffff",
      scale,
      useCORS: true,
      logging: false,
      width: node.scrollWidth,
      height: node.scrollHeight,
      windowWidth: node.scrollWidth,
      windowHeight: node.scrollHeight,
    });
    const dataUrl = canvas.toDataURL("image/png");
    return dataUrl.replace(/^data:image\/[^;]+;base64,/, "");
  } finally {
    try { iframe.remove(); } catch (_e) { /* noop */ }
  }
}

/**
 * Capture PNGs for a set of range keys against the same branch.
 * Returns `{ [rangeKey]: base64 }`. Per-range failures are logged and
 * skipped so one broken snapshot doesn't stall the rest — the backend
 * will fall back to headless Chromium for any range we don't supply.
 */
export async function captureBranchOverviewRanges(bid, rangeKeys) {
  const out = {};
  const uniq = Array.from(new Set((rangeKeys || []).filter(Boolean)));
  for (const rk of uniq) {
    try {
      out[rk] = await captureBranchOverviewPng(bid, rk);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn(`[Frontend Capture] ${bid}/${rk} failed:`, e);
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// Dispatch helpers (server-side routing wrappers)
// ---------------------------------------------------------------------------

/**
 * Fire Start-of-Day for `branchId`. Captures Branch Overview PNGs
 * in the operator's browser (same rasteriser as the download button)
 * for EVERY range declared by every active `start_of_day` rule
 * scoped to this branch — so the emailed image is byte-identical
 * to the Plane Icon (Test Email) path. Falls back to server-side
 * Chromium for any range we couldn't capture — no email is lost.
 *
 * Iter-336 · Snapshot Range Alignment. The prior build captured only
 * a single `today` PNG regardless of the rule's actual `date_ranges`,
 * so a rule configured for `yesterday` never received a matching
 * client PNG and always fell back to Chromium (functional but not
 * byte-identical to the Test Email path). This helper now mirrors
 * `dispatchTestNotification` — reading the active rule set and
 * capturing exactly the ranges the backend will iterate over.
 *
 * Returns the fresh {active_business_date} envelope the backend
 * echoes back so the caller can update its live state.
 */
export async function dispatchStartOfDay({ branchId, localToday }) {
  let snapshots = null;
  try {
    // Collect the union of date_ranges across every active
    // start_of_day rule scoped to this branch. Empty scope means
    // "all branches" so those rules apply too.
    let ranges = new Set();
    try {
      const rulesResp = await staffApi.get("/notification-rules");
      const rules = Array.isArray(rulesResp?.data) ? rulesResp.data : [];
      for (const rule of rules) {
        if (rule.active === false) continue;
        if (rule.trigger !== "start_of_day") continue;
        // Iter-336 client-side capture only applies to branch_overview
        // (brand rollups still use backend Chromium — no print route).
        if (rule.report_kind !== "branch_overview") continue;
        const scope = (rule.branch_ids && rule.branch_ids.length)
          ? rule.branch_ids
          : (rule.branch_id ? [rule.branch_id] : []);
        if (scope.length && !scope.includes(branchId)) continue;
        const dr = (Array.isArray(rule.date_ranges) && rule.date_ranges.length)
          ? rule.date_ranges
          : ["today"];
        for (const k of dr) if (k) ranges.add(k);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("SoD rule fetch failed — falling back to `today` PNG only:", e);
    }
    if (ranges.size === 0) ranges = new Set(["today"]);
    const captured = await captureBranchOverviewRanges(branchId, Array.from(ranges));
    if (captured && Object.keys(captured).length) {
      snapshots = { branch: captured };
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("Frontend snapshot capture failed — backend will fall back to Chromium:", e);
  }
  const body = { date: localToday };
  if (snapshots) body.snapshots = snapshots;
  const r = await staffApi.post(`/branches/${branchId}/business-date`, body);
  return r.data;
}

/**
 * Fire the Test Email pipeline for a given rule. For `branch_overview`
 * rules we pre-capture the PNG(s) in the browser so the previewed email
 * arrives byte-identical to the file the Owner would download. For
 * `brand_overview` (and any other kind) the backend produces the
 * snapshot itself.
 *
 * Returns the backend's dispatch envelope: {status, delivered_to,
 * resolved_recipient_count, attachment_count, …}.
 */
export async function dispatchTestNotification({ rule, catalog }) {
  let clientSnapshots = null;
  let testBranchId = null;
  if (rule && rule.report_kind === "branch_overview") {
    const scope = (rule.branch_ids && rule.branch_ids.length)
      ? rule.branch_ids
      : (rule.branch_id ? [rule.branch_id] : (catalog?.branches || []).map((b) => b.id));
    testBranchId = scope[0] || null;
    const ranges = (Array.isArray(rule.date_ranges) && rule.date_ranges.length)
      ? rule.date_ranges
      : ["today"];
    if (testBranchId) {
      try {
        toast.info("Capturing dashboard PNG…", { duration: 1500 });
        const captured = await captureBranchOverviewRanges(testBranchId, ranges);
        if (captured && Object.keys(captured).length) clientSnapshots = captured;
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("Test Email capture failed — backend will fall back to Chromium:", e);
      }
    }
  }
  const body = {};
  if (testBranchId) body.branch_id = testBranchId;
  if (clientSnapshots) body.client_snapshots = clientSnapshots;
  const r = await staffApi.post(`/notification-rules/${rule.id}/test`, body);
  return r.data;
}

/**
 * Thin shim over the backend WATI (WhatsApp Business API) webhook
 * proxy. Reserved for rules whose transport is WhatsApp rather than
 * email. Server-side wiring lives in `/api/notifications/whatsapp/*`.
 *
 * The client MUST NEVER call WATI directly — every WhatsApp message
 * flows through the backend so the audit + rate-limit + template-id
 * lookup happens server-side.
 */
export async function dispatchWhatsappViaWati({ ruleId, templateKey, recipients, payload }) {
  const body = {
    rule_id: ruleId || null,
    template_key: templateKey,
    recipients: Array.isArray(recipients) ? recipients : [],
    payload: payload || {},
  };
  const r = await staffApi.post("/notifications/whatsapp/dispatch", body);
  return r.data;
}
